// Datenzugriff der Freelancer-Verwaltung (ERP/CRM-Plan, Abschnitt 7 — Phase D1).
//
// Unrein: hier liegen ausschliesslich Supabase-Aufrufe und das
// snake_case<->camelCase-Mapping (Stil wie crmService.ts/leadService.ts:
// async, throw bei error, Mapper oben). Die Fachlichkeit — Ablauf-Ampel,
// Einsatzbereitschaft, Matching — steht pur und getestet in freelancerLogic.ts
// und wird von hier nur AUFGERUFEN, nicht nachgebaut.
//
// Tabellen, RLS und Bucket: supabase/freelancers.sql (MIGRATIONS.md Nr. 18).
//
// NICHT enthalten (kommt in D2–D5): Projekt-Freischaltung, Begehungsauftraege,
// Gutschriften. D1 ist die Admin-Sicht auf Stammdaten und Nachweise.

import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { ladeAlleSeiten } from './seitenweise';
import { Freelancer, FreelancerQualification, QualiTyp } from './freelancerLogic';
import {
    GeoQuality,
    addressKey,
    hasCoordinates,
    lookupGeoCache,
    upsertGeoCache,
} from './geoCache';
import {
    MAX_GEOCODE_ATTEMPTS,
    NominatimHit,
    classifyGeocodeResult,
    geocodeViaProxy,
    shouldRetryCacheEntry,
} from './geocodeBackfill';

/** Privater Bucket der Nachweis-Dokumente (freelancers.sql, Abschnitt 5). */
export const FREELANCER_BUCKET = 'freelancer-docs';

// ─── Row-Mapper ──────────────────────────────────────────────────────────────

const mapFreelancerRow = (row: any): Freelancer => ({
    id: row.id,
    companyId: row.company_id,
    profileUserId: row.profile_user_id ?? undefined,
    name: row.name,
    firma: row.firma ?? undefined,
    street: row.street ?? undefined,
    zip: row.zip ?? undefined,
    city: row.city ?? undefined,
    lat: row.lat != null ? Number(row.lat) : undefined,
    lng: row.lng != null ? Number(row.lng) : undefined,
    einsatzradiusKm: Number(row.einsatzradius_km ?? 50),
    satzJeBegehungCents: row.satz_je_begehung_cents != null ? Number(row.satz_je_begehung_cents) : undefined,
    steuersatzBp: Number(row.steuersatz_bp ?? 1900),
    iban: row.iban ?? undefined,
    ustIdnr: row.ust_idnr ?? undefined,
    steuernummer: row.steuernummer ?? undefined,
    datevKreditorNr: row.datev_kreditor_nr ?? undefined,
    status: row.status,
    // numeric kommt als String aus PostgREST — ohne Number() waere jeder
    // Sternevergleich ein Stringvergleich ("10" < "5").
    rating: row.rating != null ? Number(row.rating) : undefined,
    notes: row.notes ?? undefined,
    createdAt: row.created_at,
});

/**
 * Nur DEFINIERTE Felder in die Row uebernehmen, damit `updateFreelancer` mit
 * Partial<> keine Spalte versehentlich auf null setzt (gleiche Regel wie
 * toCustomerRow/toLeadRow).
 *
 * `profileUserId` steht bewusst NICHT in dieser Liste: die Verknuepfung zum
 * Login entsteht erst in D3 und darf nicht ueber ein Stammdatenformular
 * gesetzt werden koennen (Plan 3.4, Befund 3.6 — „Freelancer kann
 * profile_user_id nicht aendern"; hier gilt dasselbe fuer den Innendienst,
 * weil sonst ein Tippfehler einen fremden Login freischaltete).
 */
const toFreelancerRow = (f: Partial<Freelancer>): Record<string, any> => {
    const row: Record<string, any> = {};
    if (f.companyId !== undefined) row.company_id = f.companyId;
    if (f.name !== undefined) row.name = f.name;
    if (f.firma !== undefined) row.firma = f.firma || null;
    if (f.street !== undefined) row.street = f.street || null;
    if (f.zip !== undefined) row.zip = f.zip || null;
    if (f.city !== undefined) row.city = f.city || null;
    // Koordinaten: ein bewusst geleertes Feld muss null werden — die Zahl 0
    // schriebe sonst den Atlantik in die Karte (Muster toLeadRow).
    if (f.lat !== undefined) row.lat = Number.isFinite(f.lat) ? f.lat : null;
    if (f.lng !== undefined) row.lng = Number.isFinite(f.lng) ? f.lng : null;
    if (f.einsatzradiusKm !== undefined) row.einsatzradius_km = f.einsatzradiusKm;
    if (f.satzJeBegehungCents !== undefined) {
        row.satz_je_begehung_cents = Number.isFinite(f.satzJeBegehungCents) ? f.satzJeBegehungCents : null;
    }
    if (f.steuersatzBp !== undefined) row.steuersatz_bp = f.steuersatzBp;
    if (f.iban !== undefined) row.iban = f.iban || null;
    if (f.ustIdnr !== undefined) row.ust_idnr = f.ustIdnr || null;
    if (f.steuernummer !== undefined) row.steuernummer = f.steuernummer || null;
    if (f.datevKreditorNr !== undefined) row.datev_kreditor_nr = f.datevKreditorNr || null;
    if (f.status !== undefined) row.status = f.status;
    if (f.rating !== undefined) row.rating = Number.isFinite(f.rating) ? f.rating : null;
    if (f.notes !== undefined) row.notes = f.notes || null;
    return row;
};

const mapQualiRow = (row: any): FreelancerQualification => ({
    id: row.id,
    freelancerId: row.freelancer_id,
    typ: row.typ,
    bezeichnung: row.bezeichnung ?? undefined,
    gueltigBis: row.gueltig_bis ?? undefined,
    dokumentPfad: row.dokument_pfad ?? undefined,
});

const toQualiRow = (q: Partial<FreelancerQualification>): Record<string, any> => {
    const row: Record<string, any> = {};
    if (q.freelancerId !== undefined) row.freelancer_id = q.freelancerId;
    if (q.typ !== undefined) row.typ = q.typ;
    if (q.bezeichnung !== undefined) row.bezeichnung = q.bezeichnung || null;
    if (q.gueltigBis !== undefined) row.gueltig_bis = q.gueltigBis || null;
    if (q.dokumentPfad !== undefined) row.dokument_pfad = q.dokumentPfad || null;
    return row;
};

// ─── Mandant ─────────────────────────────────────────────────────────────────

/**
 * Die company_id des angemeldeten Profils — jeder INSERT braucht sie
 * (`company_id not null references companies(id)`).
 *
 * Bewusst clientseitig ermittelt und NICHT aus einer RPC, wie im ganzen
 * Bestand (companyScope.ts, leadService.currentCompanyId). Fehlt sie, ist das
 * Profil nicht zugeordnet (companies.sql / MIGRATIONS.md Nr. 9 noch nicht
 * gefahren) — dann bricht die Anlage hier mit einer Erklaerung ab statt
 * spaeter mit einem nackten FK-Fehler.
 */
export const currentCompanyId = async (): Promise<string> => {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) throw new Error('Nicht angemeldet — Freelancer lassen sich nicht speichern.');

    const { data, error } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .maybeSingle();
    if (error) throw new Error(describeError(error));

    const companyId = (data as any)?.company_id;
    if (!companyId) {
        throw new Error(
            'Das eigene Profil hat keine Firmenzuordnung (company_id). ' +
            'Bitte die Migration companies.sql ausführen bzw. das Profil zuordnen — ' +
            'ohne Mandant lassen sich keine Freelancer anlegen.',
        );
    }
    return String(companyId);
};

// ─── Freelancer: CRUD ────────────────────────────────────────────────────────

export interface FreelancerQuery {
    /** Nur diese Status. Leer/fehlend = alle. */
    status?: Freelancer['status'][];
}

export const fetchFreelancers = async (q: FreelancerQuery = {}): Promise<Freelancer[]> => {
    let query = supabase.from('freelancers').select('*').order('name', { ascending: true });
    if (q.status?.length) query = query.in('status', q.status);
    const { data, error } = await query;
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapFreelancerRow);
};

export const fetchFreelancer = async (id: string): Promise<Freelancer | null> => {
    const { data, error } = await supabase.from('freelancers').select('*').eq('id', id).maybeSingle();
    if (error) throw new Error(describeError(error));
    return data ? mapFreelancerRow(data) : null;
};

export type NewFreelancer = Omit<
    Freelancer,
    'id' | 'companyId' | 'createdAt' | 'einsatzradiusKm' | 'steuersatzBp' | 'status' | 'profileUserId'
> &
    Partial<Pick<Freelancer, 'companyId' | 'einsatzradiusKm' | 'steuersatzBp' | 'status'>>;

/**
 * Anlegen — inklusive Verortung.
 *
 * Die Verortung laeuft NACH dem Insert und best-effort: sie braucht Nominatim
 * (rund eine Sekunde, gelegentlich nicht erreichbar), und ein Stammsatz, der
 * an einem Netzfehler scheitert, waere fuer den Innendienst ein Raetsel. Wer
 * ohne Koordinaten angelegt wurde, taucht schlicht nicht auf der Karte auf und
 * laesst sich per „Adresse verorten" nachziehen.
 */
export const createFreelancer = async (f: NewFreelancer): Promise<Freelancer> => {
    const companyId = f.companyId || (await currentCompanyId());
    const row = toFreelancerRow({
        ...f,
        companyId,
        status: f.status ?? 'aktiv',
        einsatzradiusKm: f.einsatzradiusKm ?? 50,
        steuersatzBp: f.steuersatzBp ?? 1900,
    } as Partial<Freelancer>);

    const { data, error } = await supabase.from('freelancers').insert([row]).select('*').single();
    if (error) throw new Error(describeError(error));

    const angelegt = mapFreelancerRow(data);
    if (angelegt.lat != null && angelegt.lng != null) return angelegt;
    return (await verorteFreelancerStill(angelegt)) ?? angelegt;
};

/** Adressfelder, deren Aenderung eine neue Verortung ausloest. */
const ADRESSFELDER: (keyof Freelancer)[] = ['street', 'zip', 'city'];

/**
 * Stammdaten aendern. Bei geaenderter Adresse wird anschliessend neu verortet
 * (Plan 3.3: „Geocoding passiert bei Anlage/Adressaenderung + Backfill, nie
 * beim Rendern der Karte").
 *
 * Setzt der Aufrufer `lat`/`lng` selbst, wird NICHT nachverortet — das ist der
 * Weg fuer eine von Hand korrigierte Koordinate.
 */
export const updateFreelancer = async (
    id: string,
    updates: Partial<Freelancer>,
): Promise<Freelancer | null> => {
    const row = toFreelancerRow(updates);
    if (!Object.keys(row).length) return null;

    const { data, error } = await supabase.from('freelancers').update(row).eq('id', id).select('*').single();
    if (error) throw new Error(describeError(error));
    const aktualisiert = mapFreelancerRow(data);

    const adresseGeaendert = ADRESSFELDER.some((k) => updates[k] !== undefined);
    const koordinatenGesetzt = updates.lat !== undefined || updates.lng !== undefined;
    if (!adresseGeaendert || koordinatenGesetzt) return aktualisiert;

    return (await verorteFreelancerStill(aktualisiert)) ?? aktualisiert;
};

/** Statuswechsel aktiv/pausiert/gesperrt. Eigene Funktion, damit die
 *  Oberflaeche nicht das ganze Stammdatenobjekt schicken muss. */
export const setFreelancerStatus = async (
    id: string,
    status: Freelancer['status'],
): Promise<Freelancer | null> => updateFreelancer(id, { status });

/**
 * Loeschen. Die Nachweise gehen per `on delete cascade` mit; ihre DOKUMENTE
 * loescht die Datenbank NICHT — deshalb werden sie hier zuerst aus dem Bucket
 * geraeumt. Umgekehrte Reihenfolge hinterliesse verwaiste Dateien mit
 * personenbezogenem Inhalt.
 *
 * Ab D2 wird das nicht mehr genuegen: sobald Begehungsauftraege auf den
 * Freelancer verweisen, tritt an diese Stelle ein Archivieren (Muster
 * customers_archive_merge.sql), weil ein Loeschen dann am Fremdschluessel
 * scheitert. Solange es keine Auftraege gibt, ist Loeschen ehrlicher als ein
 * Archivflag ohne Zweck.
 */
export const deleteFreelancer = async (id: string): Promise<void> => {
    const qualis = await fetchQualifications(id);
    const pfade = qualis.map((q) => q.dokumentPfad).filter((p): p is string => !!p);
    if (pfade.length) {
        await supabase.storage.from(FREELANCER_BUCKET).remove(pfade).then(undefined, () => {
            /* best effort — die Stammdaten sollen an einem Storage-Fehler nicht haengen */
        });
    }
    const { error } = await supabase.from('freelancers').delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

// ─── Verortung ───────────────────────────────────────────────────────────────

export interface VerortungsErgebnis {
    lat: number;
    lng: number;
    quality: GeoQuality;
    /** true = aus geo_cache beantwortet, ohne Netzanfrage. */
    ausCache: boolean;
}

export interface VerortungsOptions {
    /** Geocoder-Ersatz; Standard ist Nominatim ueber /api/osm-proxy. */
    geocode?: (query: string) => Promise<NominatimHit | null>;
    maxAttempts?: number;
}

/**
 * Eine Adresse verorten — ueber denselben Cache wie der Kunden-Backfill
 * (geo_cache, Plan 3.3). Der Ablauf ist der Einzelfall des Batch-Laufs aus
 * geocodeBackfill.runGeocodeBackfill:
 *
 *   1. Cache-Treffer mit Koordinaten -> fertig, keine Netzanfrage.
 *   2. Bekannter Fehlschlag mit aufgebrauchtem Budget -> null (Handarbeit).
 *   3. Sonst Nominatim fragen, Ergebnis (auch den Fehlschlag) in den Cache.
 *
 * Wirft nur bei echten Fehlern; „nicht gefunden" ist ein `null`-Ergebnis und
 * kein Ausnahmefall. Gedrosselt werden muss hier nichts: es ist genau EINE
 * Anfrage, ausgeloest von einer Benutzeraktion.
 */
export const verorteAdresse = async (
    adresse: { street?: string; zip?: string; city?: string },
    options: VerortungsOptions = {},
): Promise<VerortungsErgebnis | null> => {
    const { norm, hash } = addressKey(adresse.street, adresse.zip, adresse.city);
    if (!norm) return null;

    const bekannt = await lookupGeoCache(hash);
    if (bekannt && hasCoordinates(bekannt)) {
        return {
            lat: bekannt.lat as number,
            lng: bekannt.lng as number,
            quality: (bekannt.quality ?? 'interpolated') as GeoQuality,
            ausCache: true,
        };
    }
    if (!shouldRetryCacheEntry(bekannt, options.maxAttempts ?? MAX_GEOCODE_ATTEMPTS)) return null;

    const street = (adresse.street || '').trim();
    const query = [street, [(adresse.zip || '').trim(), (adresse.city || '').trim()].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');

    const geocode = options.geocode ?? geocodeViaProxy;
    const treffer = classifyGeocodeResult(await geocode(query), { hasStreet: !!street });

    if (!treffer) {
        await upsertGeoCache({
            addressNorm: norm,
            addressHash: hash,
            lat: null,
            lng: null,
            provider: 'nominatim',
            quality: 'failed',
            attempts: (bekannt?.attempts ?? 0) + 1,
            error: 'Adresse nicht gefunden',
            manual: false,
            resolvedAt: null,
        });
        return null;
    }

    await upsertGeoCache({
        addressNorm: norm,
        addressHash: hash,
        lat: treffer.lat,
        lng: treffer.lng,
        provider: 'nominatim',
        quality: treffer.quality,
        attempts: (bekannt?.attempts ?? 0) + 1,
        error: null,
        manual: false,
        resolvedAt: new Date().toISOString(),
    });
    return { lat: treffer.lat, lng: treffer.lng, quality: treffer.quality, ausCache: false };
};

/**
 * Freelancer verorten und die Koordinaten schreiben. null, wenn die Adresse
 * fehlt oder nicht aufloesbar ist — der Aufrufer behaelt dann seinen Stand.
 */
export const verorteFreelancer = async (
    f: Pick<Freelancer, 'id' | 'street' | 'zip' | 'city'>,
    options: VerortungsOptions = {},
): Promise<Freelancer | null> => {
    const treffer = await verorteAdresse(f, options);
    if (!treffer) return null;

    const { data, error } = await supabase
        .from('freelancers')
        .update({ lat: treffer.lat, lng: treffer.lng })
        .eq('id', f.id)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapFreelancerRow(data);
};

/**
 * Wie `verorteFreelancer`, schluckt aber jeden Fehler. Fuer die Stellen, an
 * denen die Verortung Beiwerk einer anderen Aktion ist (Anlage, Adressaenderung):
 * dort darf ein ausgefallener Nominatim das eigentliche Speichern nicht
 * rueckgaengig erscheinen lassen.
 */
const verorteFreelancerStill = async (f: Freelancer): Promise<Freelancer | null> => {
    try {
        return await verorteFreelancer(f);
    } catch {
        return null;
    }
};

// ─── Qualifikationen ─────────────────────────────────────────────────────────

export const fetchQualifications = async (freelancerId: string): Promise<FreelancerQualification[]> => {
    const { data, error } = await supabase
        .from('freelancer_qualifications')
        .select('*')
        .eq('freelancer_id', freelancerId)
        .order('typ', { ascending: true });
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapQualiRow);
};

/**
 * Alle Nachweise auf einmal — Eingabe fuer `suggestFreelancers` und die
 * Statusspalte der Liste. Eine Abfrage je Freelancer waere ein N+1 auf einer
 * Seite, die ohnehin alle Zeilen zeigt.
 */
export const fetchAllQualifications = async (
    freelancerIds?: string[],
): Promise<FreelancerQualification[]> => {
    let query = supabase.from('freelancer_qualifications').select('*');
    if (freelancerIds) {
        if (freelancerIds.length === 0) return [];
        query = query.in('freelancer_id', freelancerIds);
    }
    const { data, error } = await query;
    if (error) throw new Error(describeError(error));
    return (data || []).map(mapQualiRow);
};

export interface NewQualification {
    freelancerId: string;
    typ: QualiTyp;
    bezeichnung?: string;
    gueltigBis?: string;
}

export const createQualification = async (q: NewQualification): Promise<FreelancerQualification> => {
    const { data, error } = await supabase
        .from('freelancer_qualifications')
        .insert([toQualiRow(q)])
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapQualiRow(data);
};

export const updateQualification = async (
    id: string,
    updates: Partial<FreelancerQualification>,
): Promise<FreelancerQualification | null> => {
    // freelancer_id ist nicht aenderbar: ein Nachweis wandert nicht von einer
    // Person zur naechsten.
    const { freelancerId: _ignoriert, ...rest } = updates;
    const row = toQualiRow(rest);
    if (!Object.keys(row).length) return null;
    const { data, error } = await supabase
        .from('freelancer_qualifications')
        .update(row)
        .eq('id', id)
        .select('*')
        .single();
    if (error) throw new Error(describeError(error));
    return mapQualiRow(data);
};

/** Loeschen samt Dokument — sonst bliebe der Scan ohne Bezug im Bucket. */
export const deleteQualification = async (q: FreelancerQualification): Promise<void> => {
    const { error } = await supabase.from('freelancer_qualifications').delete().eq('id', q.id);
    if (error) throw new Error(describeError(error));
    if (q.dokumentPfad) {
        await supabase.storage.from(FREELANCER_BUCKET).remove([q.dokumentPfad]).then(undefined, () => {
            /* best effort — die Zeile ist bereits weg */
        });
    }
};

// ─── Dokumente ───────────────────────────────────────────────────────────────

/** Erlaubte Typen — Spiegel von `allowed_mime_types` des Buckets. */
export const ERLAUBTE_DOKUMENT_TYPEN = ['application/pdf', 'image/jpeg', 'image/png'] as const;
/** 10 MB — Spiegel von `file_size_limit` des Buckets. */
export const MAX_DOKUMENT_BYTES = 10 * 1024 * 1024;

/**
 * Dateinamen auf etwas reduzieren, das als Objektschluessel taugt: Umlaute
 * und Leerzeichen sind in Storage-Pfaden zwar erlaubt, machen signierte URLs
 * aber unleserlich und stolpern ueber Encoding-Unterschiede zwischen Browsern.
 * Der ORIGINALNAME bleibt in `bezeichnung` bzw. wird beim Download wieder
 * angeboten.
 */
const sicherName = (name: string): string => {
    const roh = (name || 'dokument').trim().toLowerCase();
    const ersetzt = roh
        .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
        .replace(/[^a-z0-9._-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
    return ersetzt || 'dokument';
};

/**
 * Nachweis-Dokument hochladen.
 *
 * Pfadschema `<freelancer_id>/<quali_id>/<dateiname>` (freelancers.sql). Das
 * fuehrende Praefix ist kein Ordnungsdetail, sondern die Grundlage der
 * Storage-Policies in D3: der Freelancer bekommt dort Zugriff auf genau den
 * Pfad, der mit SEINER id beginnt. Wer das Schema aendert, muss die Policies
 * mitaendern.
 *
 * `upsert: true` — ein erneuter Upload derselben Datei zu demselben Nachweis
 * ist die Korrektur eines Scans, kein Fehler. (Anders als beim Rechnungs-
 * Bucket, wo ein zweiter Schreibversuch scheitern MUSS: dort haengt die
 * GoBD-Unveraenderbarkeit daran.)
 */
export const uploadQualificationDocument = async (
    freelancerId: string,
    qualiId: string,
    file: File,
): Promise<FreelancerQualification | null> => {
    if (!file) throw new Error('Keine Datei ausgewählt.');
    if (file.size > MAX_DOKUMENT_BYTES) {
        throw new Error('Die Datei ist größer als 10 MB. Bitte den Scan verkleinern.');
    }
    if (file.type && !(ERLAUBTE_DOKUMENT_TYPEN as readonly string[]).includes(file.type)) {
        throw new Error('Nur PDF, JPG oder PNG sind als Nachweis zulässig.');
    }

    const pfad = `${freelancerId}/${qualiId}/${sicherName(file.name)}`;
    const { error: upErr } = await supabase.storage
        .from(FREELANCER_BUCKET)
        .upload(pfad, file, { upsert: true, contentType: file.type || 'application/octet-stream' });
    if (upErr) throw new Error(describeError(upErr));

    try {
        return await updateQualification(qualiId, { dokumentPfad: pfad });
    } catch (e) {
        // Der Verweis konnte nicht gesetzt werden — die Datei waere sonst
        // unauffindbar im Bucket. Best-effort wieder entfernen.
        await supabase.storage.from(FREELANCER_BUCKET).remove([pfad]).then(undefined, () => { /* egal */ });
        throw e;
    }
};

/**
 * Kurzlebige signierte URL zum Ansehen/Herunterladen (Plan 3.4: „kurzlebige
 * signierte URLs"). Standard sind 5 Minuten — lang genug fuer einen Klick,
 * kurz genug, dass eine weitergereichte URL nichts mehr taugt.
 */
export const getQualificationDocumentUrl = async (
    dokumentPfad: string,
    expiresInSeconds = 300,
): Promise<string> => {
    if (!dokumentPfad) throw new Error('Zu diesem Nachweis ist kein Dokument hinterlegt.');
    const { data, error } = await supabase.storage
        .from(FREELANCER_BUCKET)
        .createSignedUrl(dokumentPfad, expiresInSeconds);
    if (error) throw new Error(describeError(error));
    const url = data?.signedUrl ?? '';
    if (!url) throw new Error('Das Dokument ließ sich nicht öffnen.');
    return url;
};

/** Dokument entfernen, den Nachweis aber behalten. */
export const removeQualificationDocument = async (
    q: FreelancerQualification,
): Promise<FreelancerQualification | null> => {
    if (!q.dokumentPfad) return q;
    const aktualisiert = await updateQualification(q.id, { dokumentPfad: '' });
    await supabase.storage.from(FREELANCER_BUCKET).remove([q.dokumentPfad]).then(undefined, () => {
        /* best effort */
    });
    return aktualisiert;
};

// ─── Projekte als Bezugspunkte des Matchings ────────────────────────────────

export interface MatchProjektPunkt {
    id: string;
    name: string;
    projectNumber?: string;
    address?: string;
    lat: number;
    lng: number;
}

/**
 * Verortete, nicht archivierte Projekte — die Auswahlliste des
 * Matching-Panels.
 *
 * Nur die ausgewerteten JSON-Pfade, nicht `data`: die ganze Spalte waren auf
 * PROD 16 MB fuer 610 Projekte (23.10.2026). Koordinaten als Objekt (`->`),
 * nicht als `->>lat` — ein fehlendes lat wuerde sonst zu Text-null und ueber
 * Number() zu 0. Seitenweise: projects waechst auf die stille 1000er-Grenze
 * von PostgREST zu.
 */
export const fetchProjektPunkte = async (): Promise<MatchProjektPunkt[]> => {
    type Zeile = {
        id: unknown;
        name?: string | null;
        project_number?: string | null;
        koordinaten?: { lat?: unknown; lng?: unknown } | null;
        metaName?: string | null;
        metaNummer?: string | null;
        adresse?: string | null;
    };
    const rows = await ladeAlleSeiten<Zeile>((von, bis) =>
        supabase
            .from('projects')
            .select('id, name, project_number, koordinaten:data->metadata->globalData->locationCoordinates, '
                + 'metaName:data->metadata->>projectName, metaNummer:data->metadata->>projectNumber, '
                + 'adresse:data->metadata->>projectAddress')
            .neq('status', 'archived')
            .order('id')
            .range(von, bis))
        .catch((error: unknown) => { throw new Error(describeError(error)); });

    const out: MatchProjektPunkt[] = [];
    for (const row of rows) {
        const lat = Number(row.koordinaten?.lat);
        const lng = Number(row.koordinaten?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        if (lat === 0 && lng === 0) continue;
        out.push({
            id: String(row.id),
            name: String(row.name || row.metaName || 'Unbenanntes Projekt'),
            projectNumber: String(row.project_number || row.metaNummer || '') || undefined,
            address: row.adresse || undefined,
            lat,
            lng,
        });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name, 'de'));
};
